/**
 * Meta Pixel Integration
 *
 * Loads and provides helpers for the Meta (Facebook) Pixel.
 * Pixel ID is read from VITE_META_PIXEL_ID env variable.
 */

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
  }
}

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

let initialized = false;

/**
 * Initialize the Meta Pixel. Call once at app startup.
 * Injects the fbq script and fires the initial PageView.
 */
export function initMetaPixel(): void {
  if (initialized || !PIXEL_ID) return;
  initialized = true;

  // Meta Pixel base code (standard fbevents.js loader)
  const f = window;
  const b = document;

  if (f.fbq) return; // already loaded by something else

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n: any = (f.fbq = function (...args: unknown[]) {
    if (n.callMethod) { n.callMethod(...args); } else { n.queue.push(args); }
  });

  if (!f._fbq) f._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];

  const s = b.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = b.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(s, firstScript);

  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
}

/**
 * Track a standard Meta Pixel event.
 */
export function trackPixelEvent(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (!PIXEL_ID || typeof window.fbq !== 'function') return;
  if (params) {
    window.fbq('track', eventName, params);
  } else {
    window.fbq('track', eventName);
  }
}

/**
 * Track a custom Meta Pixel event.
 */
export function trackPixelCustomEvent(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (!PIXEL_ID || typeof window.fbq !== 'function') return;
  if (params) {
    window.fbq('trackCustom', eventName, params);
  } else {
    window.fbq('trackCustom', eventName);
  }
}
